import type { OpportunityStage } from "@/data/opportunityTypes";
import type { AppointmentResultStatus, AppointmentResultValue } from "@/services/appointment-result.service";

export type LeadAppointmentCsvRow = Record<string, string>;

export const LEAD_APPOINTMENT_TEMPLATE_COLUMNS = [
  "external_id",
  "first_name",
  "last_name",
  "name",
  "primary_phone",
  "primary_email",
  "address",
  "city",
  "state",
  "zip",
  "project_type",
  "install_type",
  "lead_source",
  "lead_cost",
  "sales_rep",
  "sales_rep_email",
  "received_at",
  "accepted_at",
  "rejected_at",
  "lead_stage",
  "appointment_date",
  "appointment_time",
  "appointment_status",
  "appointment_result",
  "close_status",
  "closed_at",
  "lost_reason",
  "notes",
];

const LEAD_APPOINTMENT_TEMPLATE_SAMPLE_ROWS: LeadAppointmentCsvRow[] = [
  {
    external_id: "OLD-LEAD-1001",
    first_name: "Ava",
    last_name: "Client",
    name: "",
    primary_phone: "555-0100",
    primary_email: "ava.client@example.com",
    address: "123 Palm Ave",
    city: "Tampa",
    state: "FL",
    zip: "33602",
    project_type: "Shower Remodel",
    install_type: "",
    lead_source: "Old CRM",
    lead_cost: "125",
    sales_rep: "rep@example.com",
    sales_rep_email: "rep@example.com",
    received_at: "2026-06-18",
    accepted_at: "2026-06-19",
    rejected_at: "",
    lead_stage: "",
    appointment_date: "",
    appointment_time: "",
    appointment_status: "",
    appointment_result: "",
    close_status: "sold",
    closed_at: "2026-06-20",
    lost_reason: "",
    notes: "Closed won lead only. No appointment row will be created.",
  },
  {
    external_id: "OLD-APPT-2001",
    first_name: "Nora",
    last_name: "Demo",
    name: "",
    primary_phone: "555-0111",
    primary_email: "nora.demo@example.com",
    address: "456 Bay St",
    city: "Sarasota",
    state: "FL",
    zip: "34236",
    project_type: "",
    install_type: "Full Bathroom Remodel",
    lead_source: "Old CRM",
    lead_cost: "95.50",
    sales_rep: "rep@example.com",
    sales_rep_email: "rep@example.com",
    received_at: "2026-06-21",
    accepted_at: "",
    rejected_at: "2026-06-22",
    lead_stage: "",
    appointment_date: "2026-06-22",
    appointment_time: "10:30 AM",
    appointment_status: "completed",
    appointment_result: "no sale",
    close_status: "",
    closed_at: "",
    lost_reason: "Price",
    notes: "Appointment row will be created because appointment fields are present.",
  },
];

export interface LeadAppointmentImportRecord {
  externalId: string;
  customerName: string;
  phone: string;
  email: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  projectType: string;
  leadSource: string;
  leadCost: number | null;
  salesRep: string;
  receivedAt?: string;
  acceptedAt?: string;
  rejectedAt?: string;
  stage: OpportunityStage;
  notes: string;
  closedAt?: string;
  lostReason?: string;
  appointment?: {
    appointmentId: string;
    scheduledAt: string;
    status: AppointmentResultStatus;
    result: AppointmentResultValue;
  };
  warnings: string[];
}

function safeString(value: unknown): string {
  return String(value ?? "").trim();
}

function firstString(...values: unknown[]): string {
  for (const value of values) {
    const s = safeString(value);
    if (s) return s;
  }
  return "";
}

function rowValue(row: LeadAppointmentCsvRow, ...keys: string[]): string {
  for (const key of keys) {
    const direct = firstString(row[key]);
    if (direct) return direct;
    const normalized = key.toLowerCase().replace(/[^a-z0-9]+/g, "");
    const match = Object.entries(row).find(([candidate]) => candidate.toLowerCase().replace(/[^a-z0-9]+/g, "") === normalized);
    if (match) {
      const value = firstString(match[1]);
      if (value) return value;
    }
  }
  return "";
}

function customFieldValue(row: LeadAppointmentCsvRow, ...keys: string[]): string {
  const raw = rowValue(row, "custom_fields", "customFields", "custom_fields_json");
  if (!raw) return "";
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (Array.isArray(parsed)) {
      const wanted = new Set(keys.map((key) => key.toLowerCase()));
      for (const field of parsed) {
        const item = field as Record<string, unknown>;
        const fieldKey = firstString(item.key, item.name, item.label).toLowerCase();
        if (wanted.has(fieldKey)) return safeString(item.value);
      }
    }
    if (parsed && typeof parsed === "object") {
      const obj = parsed as Record<string, unknown>;
      for (const key of keys) {
        const value = firstString(obj[key]);
        if (value) return value;
      }
    }
  } catch {
    return "";
  }
  return "";
}

function normalizeDateTime(dateOrDateTime: string, time?: string): string {
  const raw = [dateOrDateTime, time].map((value) => safeString(value)).filter(Boolean).join(" ");
  if (!raw) return "";
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) return parsed.toISOString();
  return "";
}

function normalizeMoney(value: string): number | null {
  const cleaned = value.replace(/[$,\s]/g, "");
  if (!cleaned) return null;
  const parsed = Number(cleaned);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalizeAppointmentStatus(raw: string): AppointmentResultStatus | undefined {
  const key = raw.toLowerCase().replace(/[_-]+/g, " ").trim();
  if (!key) return undefined;
  if (["scheduled", "booked", "set", "confirmed", "appointment scheduled", "appointment set"].includes(key)) return "scheduled";
  if (["completed", "complete", "ran", "demoed", "appointment completed"].includes(key)) return "completed";
  if (["cancelled", "canceled", "cancel"].includes(key)) return "cancelled";
  if (["no show", "noshow"].includes(key)) return "no_show";
  if (["rescheduled", "reschedule"].includes(key)) return "rescheduled";
  return undefined;
}

function normalizeAppointmentResult(raw: string): AppointmentResultValue | undefined {
  const key = raw.toLowerCase().replace(/[_-]+/g, " ").trim();
  if (!key) return undefined;
  if (["booked", "sold", "won", "closed won", "sale"].includes(key)) return "booked";
  if (["not interested", "no sale", "lost", "closed lost", "declined"].includes(key)) return "not_interested";
  if (["needs more time", "think about it", "follow up"].includes(key)) return "needs_more_time";
  if (["wrong number", "bad phone"].includes(key)) return "wrong_number";
  if (["voicemail", "left voicemail"].includes(key)) return "voicemail";
  if (["deferred", "future", "later"].includes(key)) return "deferred";
  return undefined;
}

function normalizeLeadStage(input: {
  rawStage: string;
  closeStatus: string;
  appointmentTime: string;
  appointmentStatus?: AppointmentResultStatus;
  appointmentResult?: AppointmentResultValue;
}): OpportunityStage {
  const key = firstString(input.rawStage, input.closeStatus).toLowerCase().replace(/[_-]+/g, " ").trim();
  if (["closed won", "won", "sold", "booked"].includes(key)) return "Closed Won";
  if (["closed lost", "lost", "no sale", "no demo", "cancelled", "canceled"].includes(key)) return "Closed Lost";
  if (["follow up", "follow up needed", "needs more time"].includes(key) || input.appointmentResult === "needs_more_time") return "Follow-Up Needed";
  if (["appointment completed", "completed", "demoed", "ran"].includes(key) || input.appointmentStatus === "completed") return "Appointment Completed";
  if (["appointment scheduled", "scheduled", "appointment set"].includes(key) || input.appointmentStatus === "scheduled" || input.appointmentTime) return "Appointment Scheduled";
  if (["quote created", "quoted", "estimate"].includes(key)) return "Quote Created";
  return "Lead";
}

export function parseLeadAppointmentCsv(text: string): LeadAppointmentCsvRow[] {
  const rows: string[][] = [];
  let field = "";
  let row: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    const next = text[i + 1];
    if (ch === '"') {
      if (inQuotes && next === '"') {
        field += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === "," && !inQuotes) {
      row.push(field);
      field = "";
    } else if ((ch === "\n" || ch === "\r") && !inQuotes) {
      if (ch === "\r" && next === "\n") i += 1;
      row.push(field);
      if (row.some((cell) => cell.trim())) rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }

  if (field || row.length) {
    row.push(field);
    if (row.some((cell) => cell.trim())) rows.push(row);
  }

  const [headers = [], ...body] = rows;
  const normalizedHeaders = headers.map((header) => header.trim());
  return body.map((cells) => normalizedHeaders.reduce<LeadAppointmentCsvRow>((acc, header, index) => {
    acc[header] = cells[index]?.trim() ?? "";
    return acc;
  }, {}));
}

export function mapLeadAppointmentCsvRow(row: LeadAppointmentCsvRow): LeadAppointmentImportRecord {
  const firstName = rowValue(row, "first_name", "firstName");
  const lastName = rowValue(row, "last_name", "lastName");
  const customerName = firstString(
    rowValue(row, "customer_name", "client_name", "primary_name", "name"),
    [firstName, lastName].filter(Boolean).join(" "),
  );
  const phone = rowValue(row, "primary_phone", "phone", "mobile", "mobile_phone");
  const email = rowValue(row, "primary_email", "email");
  const appointmentDate = rowValue(row, "appointment_at", "appointment_datetime", "scheduled_at", "appointment_date");
  const appointmentTimeOnly = rowValue(row, "appointment_time");
  const appointmentTime = normalizeDateTime(appointmentDate, appointmentTimeOnly);
  const appointmentStatus = normalizeAppointmentStatus(rowValue(row, "appointment_status"));
  const appointmentResult = normalizeAppointmentResult(rowValue(row, "appointment_result", "outcome", "result"));
  const closeStatus = rowValue(row, "close_status", "closed_status");
  const stage = normalizeLeadStage({
    rawStage: rowValue(row, "lead_stage", "pipeline_stage", "stage", "status"),
    closeStatus,
    appointmentTime,
    appointmentStatus,
    appointmentResult,
  });
  const closedAt = normalizeDateTime(rowValue(row, "closed_at", "won_at", "lost_at"));
  const externalId = rowValue(row, "external_id", "lead_id", "project_id", "id");
  const projectType = firstString(
    rowValue(row, "project_type", "install_type"),
    customFieldValue(row, "project_type", "install_type"),
    "Other",
  );
  const salesRep = rowValue(
    row,
    "assigned_rep",
    "sales_rep",
    "sales_rep_email",
    "rep",
    "rep_email",
    "salesperson",
    "salesperson_email",
    "appointment_rep",
    "appointment_rep_email",
  );
  const receivedAt = normalizeDateTime(rowValue(row, "received_at", "lead_received_at", "created_at", "date_received"));
  const acceptedAt = normalizeDateTime(rowValue(row, "accepted_at", "lead_accepted_at", "approved_at", "accepted_date"));
  const rejectedAt = normalizeDateTime(rowValue(row, "rejected_at", "rejeted_at", "lead_rejected_at", "declined_at", "rejected_date"));
  const leadCostRaw = rowValue(row, "lead_cost", "cost", "source_cost", "acquisition_cost");
  const leadCost = normalizeMoney(leadCostRaw);
  const notes = rowValue(row, "notes", "note");
  const lostReason = rowValue(row, "lost_reason", "no_sale_reason", "disposition_reason");
  const hasAppointment = Boolean(appointmentTime || appointmentStatus || appointmentResult);
  const warnings: string[] = [];

  if (!customerName) warnings.push("Missing customer name");
  if (!phone && !email) warnings.push("Missing phone/email");
  if (hasAppointment && !appointmentTime) warnings.push("Appointment has status/result but no scheduled time");
  if (leadCostRaw && leadCost === null) warnings.push(`Invalid lead cost "${leadCostRaw}"`);

  return {
    externalId,
    customerName: customerName || "Unknown Customer",
    phone,
    email,
    address: rowValue(row, "address", "street", "address_line1"),
    city: rowValue(row, "city"),
    state: rowValue(row, "state") || "FL",
    zip: rowValue(row, "zip", "postal_code", "postal"),
    projectType,
    leadSource: rowValue(row, "lead_source", "source", "provider") || "migration",
    leadCost,
    salesRep,
    receivedAt: receivedAt || undefined,
    acceptedAt: acceptedAt || undefined,
    rejectedAt: rejectedAt || undefined,
    stage,
    notes: [notes, lostReason ? `Lost reason: ${lostReason}` : ""].filter(Boolean).join("\n\n"),
    closedAt: closedAt || undefined,
    lostReason: lostReason || undefined,
    appointment: hasAppointment
      ? {
          appointmentId: `import-${externalId || customerName || "lead"}-${appointmentTime || Date.now()}`.replace(/[^a-zA-Z0-9-]+/g, "-"),
          scheduledAt: appointmentTime || closedAt || new Date().toISOString(),
          status: appointmentStatus ?? (appointmentResult ? "completed" : "scheduled"),
          result: appointmentResult ?? null,
        }
      : undefined,
    warnings,
  };
}

export function mapLeadAppointmentCsv(text: string): LeadAppointmentImportRecord[] {
  return parseLeadAppointmentCsv(text).map(mapLeadAppointmentCsvRow);
}

export function leadAppointmentTemplateCsv(): string {
  const rows = [
    LEAD_APPOINTMENT_TEMPLATE_COLUMNS,
    ...LEAD_APPOINTMENT_TEMPLATE_SAMPLE_ROWS.map((sample) =>
      LEAD_APPOINTMENT_TEMPLATE_COLUMNS.map((column) => sample[column] ?? ""),
    ),
  ];
  return rows.map((row) => row.map(csvCell).join(",")).join("\n") + "\n";
}

function csvCell(value: unknown): string {
  const raw = safeString(value);
  if (!/[",\n\r]/.test(raw)) return raw;
  return `"${raw.replace(/"/g, '""')}"`;
}
